import type { Difficulty, IdeaCategory, PromptDeployTarget, SavedStatus, VibeCodingTool } from "./types";

export const APP_NAME = "VibeGenie";
export const ANONYMOUS_USER_ID_KEY = "vibegenie_anonymous_user_id";
export const THEME_KEY = "vibegenie_theme";
export const DEFAULT_AI_MODEL = "@cf/openai/gpt-oss-120b";
export const DEEP_DIVE_DEFAULT_AI_MODEL = "@cf/openai/gpt-oss-20b";
export const DEFAULT_PROMPT_DEPLOY_TARGET: PromptDeployTarget = "vercel";

export type AiModelOption = {
  model: string;
  name: string;
  provider: string;
  recommendedUse: string;
  inputUsdPerMillionTokens: number;
  outputUsdPerMillionTokens: number;
};

export const AI_MODEL_OPTIONS: AiModelOption[] = [
  {
    model: "@cf/openai/gpt-oss-120b",
    name: "gpt-oss 120B",
    provider: "OpenAI",
    recommendedUse: "品質重視。深掘り相談、要件整理、Codex向け実装プロンプト",
    inputUsdPerMillionTokens: 0.35,
    outputUsdPerMillionTokens: 0.75
  },
  {
    model: "@cf/openai/gpt-oss-20b",
    name: "gpt-oss 20B",
    provider: "OpenAI",
    recommendedUse: "高速・低コスト。アイデア生成、短めの改善、軽い要約",
    inputUsdPerMillionTokens: 0.2,
    outputUsdPerMillionTokens: 0.3
  },
  {
    model: "@cf/google/gemma-4-26b-a4b-it",
    name: "Gemma 4 26B A4B",
    provider: "Google",
    recommendedUse: "長文・画像理解も見据えた標準モデル",
    inputUsdPerMillionTokens: 0.1,
    outputUsdPerMillionTokens: 0.3
  },
  {
    model: "@cf/zai-org/glm-4.7-flash",
    name: "GLM-4.7-Flash",
    provider: "Zhipu AI",
    recommendedUse: "低コストな長文/多言語モデル。大量生成や反復用途",
    inputUsdPerMillionTokens: 0.06,
    outputUsdPerMillionTokens: 0.4
  },
  {
    model: "@cf/moonshotai/kimi-k2.6",
    name: "Kimi K2.6",
    provider: "Moonshot AI",
    recommendedUse: "最上位の長文・エージェント用途。高品質だが出力コスト高め",
    inputUsdPerMillionTokens: 0.95,
    outputUsdPerMillionTokens: 4
  }
];

export const getAiModelOption = (model: string) => AI_MODEL_OPTIONS.find((option) => option.model === model);

export const CATEGORY_LABELS: Record<IdeaCategory, string> = {
  ai_tool: "AIツール",
  web_app: "Webアプリ",
  ios_app: "iOSアプリ",
  saas: "仕事向けサービス",
  productivity: "業務効率化",
  game: "ゲーム",
  lifestyle: "ライフスタイル",
  education: "学習",
  developer_tool: "開発者ツール"
};

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  beginner: "初心者向け",
  intermediate: "中級",
  advanced: "上級"
};

export const TOOL_LABELS: Record<VibeCodingTool, string> = {
  codex: "Codex",
  cursor: "Cursor",
  claude_code: "Claude Code",
  windsurf: "Windsurf",
  lovable: "Lovable",
  bolt: "Bolt",
  replit: "Replit",
  v0: "v0",
  unknown: "まだ決めていない"
};

export const PROMPT_DEPLOY_TARGET_LABELS: Record<PromptDeployTarget, string> = {
  vercel: "Vercel",
  cloudflare_workers: "Cloudflare Workers",
  cloudflare_pages: "Cloudflare Pages"
};

export const SAVED_STATUS_LABELS: Record<SavedStatus, string> = {
  liked: "保存済み",
  later: "保存済み",
  building: "作成中",
  completed: "完成"
};

export const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
  value: value as IdeaCategory,
  label
}));

export const TOOL_OPTIONS = Object.entries(TOOL_LABELS).map(([value, label]) => ({
  value: value as VibeCodingTool,
  label
}));

export const PROMPT_DEPLOY_TARGET_OPTIONS = Object.entries(PROMPT_DEPLOY_TARGET_LABELS).map(([value, label]) => ({
  value: value as PromptDeployTarget,
  label
}));

export const DIFFICULTY_OPTIONS = Object.entries(DIFFICULTY_LABELS).map(([value, label]) => ({
  value: value as Difficulty,
  label
}));
